import { useEffect, useState } from 'react';

import {
  type ComparePane,
  type ComparePaneState,
  type CompareViewUIEvents,
  MedicalImageRender,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

type CompareTransformState = CompareViewUIEvents['compareTransformChanged'];
type ComparePaneStates = Record<ComparePane, ComparePaneState>;

const PRISTINE: CompareTransformState = {
  isPristine: true,
  hasAdjustment: false,
};

// A fresh view has no image assigned to either pane
const EMPTY_PANE_STATES: ComparePaneStates = {
  left: 'empty',
  right: 'empty',
};

// Owns the renderer's compare view for the time the dialog is open and mirrors
// its transform state, which drives the Reset buttons, and the per-pane load
// state, which drives the loading indicators.
export const useCompareView = (enabled: boolean) => {
  const [isViewReady, setIsViewReady] = useState(false);
  const [transform, setTransform] = useState(PRISTINE);
  const [paneStates, setPaneStates] = useState(EMPTY_PANE_STATES);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // The renderer starts with the first report page; the profile may open
    // the dialog before any of them. The report orchestrator only sets a
    // page up while the renderer is stopped, so a launch made here is undone
    // when the dialog closes.
    const launchedHere = !MedicalImageRender.isLaunched();
    if (launchedHere) {
      MedicalImageRender.launch();
    }
    const removeView = MedicalImageRender.CompareAPI.addCompareView();
    const removeListener =
      MedicalImageRender.CompareAPI.addCompareEventListener(
        'compareTransformChanged',
        ({ isPristine, hasAdjustment }) =>
          setTransform({ isPristine, hasAdjustment }),
      );
    const removeStateListener =
      MedicalImageRender.CompareAPI.addCompareEventListener(
        'comparePaneStateChanged',
        ({ pane, state }) =>
          setPaneStates((current) =>
            current[pane] === state ? current : { ...current, [pane]: state },
          ),
      );
    setIsViewReady(true);

    return () => {
      removeListener?.();
      removeStateListener?.();
      removeView?.();
      if (launchedHere) {
        MedicalImageRender.shutdown();
      }
      setIsViewReady(false);
      setTransform(PRISTINE);
      setPaneStates(EMPTY_PANE_STATES);
    };
  }, [enabled]);

  return { isViewReady, transform, paneStates };
};
